/**
 * API共通型定義
 *
 * 全APIで共通して使用されるレスポンス型を定義します。
 * OpenAPI 3.0.3仕様に準拠しています。
 *
 * @example 単一エラーの処理
 * ```typescript
 * const response = await apiCall();
 * if (isApiError(response)) {
 *   console.error(response.error.message);
 * }
 * ```
 *
 * @example 複数バリデーションエラーの処理
 * ```typescript
 * const response = await apiCall();
 * if (isApiError(response) && hasMultipleErrors(response)) {
 *   const allMessages = getAllErrorMessages(response);
 *   const errorsByField = getErrorsByField(response);
 *
 *   // フィールド別にエラーを表示
 *   Object.entries(errorsByField).forEach(([field, messages]) => {
 *     console.error(`${field}: ${messages.join(', ')}`);
 *   });
 * }
 * ```
 */

/**
 * API成功レスポンスの基本型
 */
export interface ApiSuccessResponse<T = unknown> {
  success: true;
  data: T;
  message?: string;
  timestamp: string;
}

/**
 * APIエラーレスポンス
 *
 * すべてのエラーレスポンスはこの形式に従います。
 * 複数のバリデーションエラーが発生した場合は、errors配列に格納されます。
 */
export interface ApiErrorResponse {
  success: false;
  error: {
    /** エラーコード（例: VALIDATION_ERROR, PDF_NOT_FOUND） */
    code: string;
    /** エラーメッセージ */
    message: string;
    /** エラーの詳細情報（省略可能） */
    details?: unknown;
    /** エラーが発生したフィールド（省略可能） */
    field?: string;
    /** リトライ可能かどうか（省略可能） */
    retryable?: boolean;
  };
  /** 複数のエラー（バリデーションエラーなど）（省略可能） */
  errors?: Array<{
    /** エラーコード */
    code: string;
    /** エラーメッセージ */
    message: string;
    /** エラーの詳細情報（省略可能） */
    details?: unknown;
    /** エラーが発生したフィールド（バリデーションエラー時など） */
    field?: string;
  }>;
  /** レスポンスのタイムスタンプ（ISO形式） */
  timestamp: string;
}

/**
 * レスポンスが複数のバリデーションエラーを含むかどうかを判定する型ガード
 */
export function hasMultipleErrors(response: ApiErrorResponse): boolean {
  return Array.isArray(response.errors) && response.errors.length > 0;
}

/**
 * エラーレスポンスからすべてのエラーメッセージを取得
 *
 * @param response エラーレスポンス
 * @returns エラーメッセージの配列
 *
 * @example
 * ```typescript
 * const messages = getAllErrorMessages(errorResponse);
 * // ["Validation failed", "Email is required", "Password is too short"]
 * ```
 */
export function getAllErrorMessages(response: ApiErrorResponse): string[] {
  const messages: string[] = [response.error.message];

  if (response.errors && response.errors.length > 0) {
    messages.push(...response.errors.map((err) => err.message));
  }

  return messages;
}

/**
 * エラーレスポンスからフィールド別のエラーマップを取得
 *
 * @param response エラーレスポンス
 * @returns フィールド名をキーとしたエラーメッセージのマップ
 *
 * @example
 * ```typescript
 * const errors = getErrorsByField(errorResponse);
 * // {
 * //   "email": ["Email is required", "Email format is invalid"],
 * //   "password": ["Password is too short"]
 * // }
 *
 * // フォームのバリデーションエラー表示に使用
 * setFieldError("email", errors.email?.[0]);
 * ```
 */
export function getErrorsByField(response: ApiErrorResponse): Record<string, string[]> {
  const errorsByField: Record<string, string[]> = {};

  // メインエラーにfieldがある場合
  if (response.error.field) {
    errorsByField[response.error.field] = [response.error.message];
  }

  // 複数エラーがある場合
  if (response.errors) {
    for (const error of response.errors) {
      if (error.field) {
        if (!errorsByField[error.field]) {
          errorsByField[error.field] = [];
        }
        errorsByField[error.field].push(error.message);
      }
    }
  }

  return errorsByField;
}
